// In some cases the page will want to scroll.
// export const PageCanScroll = atom<boolean>({
//   key: "VS_PageCanScroll",
//   default: false,
// });

import { FilterName, FilterSelector } from "./filter";
import type { PageElementData, PageSizeAtomType, TapIdContainer } from "../@types/viewerati";
import { atom, atomFamily, selector } from "recoil";

import type { ElementProps } from "../functionalComponents/RenderPage";
import type { ReactElement } from "react";
import { NavigationType } from "./navigation";
import { getPageNumberFromUrl } from "./getPageNumberFromUrl";

export const pageHasWidescreenState = atomFamily<boolean, number>({
	key: "pageHasWidescreen",
	default: false,
});

// use pageindex for key
export const ContentIsOverflowingForPage = atomFamily<boolean, number>({
  key: "VS_ContentIsOverflowingForPage",
  default: false
})

export const createLocation = (pageIdx: number, atomId: number) => `${pageIdx}::${atomId}`




export const IsInPlayWrightTest = selector<boolean>({
  key: "VS_IsInPlayWrightTest",
  get: () => {
    return import.meta.env.VITE_PLAYWRIGHT === "true"
  }
})

export const StickyHeaderHeightAtom = atom<number>({
  key: "VS_StickyHeaderHeight",
  default: 0,
});

export const StickyFooterHeightAtom = atom<number>({
  key: "VS_StickyFooterHeight",
  default: 0,
});


// how many columns are in the page right now?
export const PageColumns = atom<number>({
  key: "VS_PageColumns",
  default: 1,
});

interface PageElementsLocationAndData { pageIndex: number; atomId: number, key: string, data: PageElementData }

// the state for each element in a page
// as recoil doesn't allow for atom destruction there can be more in the family than appear on screen.
// key derived from createLocation function
export const PageElementsFamily = atomFamily<PageElementsLocationAndData, string>({
  key: "VS_PageElements",
  default: {
    pageIndex: 0,
    atomId: 0,
    key: "0",
    data: {
      background: "unset",
      content: "",
      firstElement: false,
      id: -1,
      lastElement: false,
      tapped: false,
      type: "blank:",
    },
  }
});

// the loaded state for each element in a page
// change it to false of the element is a media element
// key derived from createLocation function

export const PageElementIsLoadedFamily = atomFamily<boolean, string>({
  key: "VS_PageElementIsLoadedFamily",
  default: false,
});

// natural aspect ratios (width/height) of loaded media, keyed by atomId
export const PageMediaAspectsFamily = atomFamily<Record<number, number>, number>({
  key: "VS_PageMediaAspects",
  default: {},
});

// pageIndex is base 1.
export const PageIndex = atom<number>({
  key: "VS_PageIndex",
  default: getPageNumberFromUrl
});

/** element last tapped in author mode (click-to-edit) — drives the selection outline */
export const AuthorSelectedElementAtom = atom<{ pageIndex: number; atomId: number } | null>({
  key: "VS_AuthorSelectedElement",
  default: null,
});

/**
 * last page set by an incoming goToPage message — lets the pop-out tell
 * locally-driven page turns (report to author) from remote ones (don't echo)
 */
export const LastRemotePageAtom = atom<number | null>({
  key: "VS_LastRemotePage",
  default: null,
});

export const PageScrollPercentage = atom<number>({
  key: "VS_PageScrollPercentage",
  default: 0,
});


// how many rows are in the page right now?
export const PageRows = atom<number>({
  key: "VS_PageRows",
  default: 1,
});

export const PageSizeAtom = atom<PageSizeAtomType>({
  key: "VS_PageSize",
  default: {
    onePercentHeight: 0,
    onePercentWidth: 0,
    baseFontSize: 10,
    baseFontSizeRaw: 10,
    containerX: 0,
    containerY: 0,
    documentHeight: 0,
    documentWidth: 0,
    forceSingleColumn: false,
    gridXWidth: 0,
    gridYHeight: 0,
    height: 0,
    landscape: true,
    width: 0,
    x0: 0,
    y0: 0,
  },
});

// the page size after padding has been taken into account
export const PageSizeWithPaddingSelector = selector<PageSizeAtomType>({
  key: "VS_PageSizeWithPaddingSelector",
  get: ({ get }) => {
    const pageSize = get(PageSizeAtom);
    const navigationType = get(NavigationType)
    const filterName = get(FilterName);
    const filter = get(FilterSelector(filterName));
    const framePadding = filter.frame;
    const paddingBlockStart = framePadding?.paddingBlockStart ? framePadding.paddingBlockStart * pageSize.baseFontSizeRaw : 0;
    const paddingBlockEnd = framePadding?.paddingBlockEnd
      ? framePadding.paddingBlockEnd * pageSize.baseFontSizeRaw
      : 0;
    const paddingInlineStart = framePadding?.paddingInlineStart ? framePadding.paddingInlineStart * pageSize.baseFontSizeRaw : 0;
    const paddingInlineEnd = framePadding?.paddingInlineEnd ? framePadding.paddingInlineEnd * pageSize.baseFontSizeRaw : 0;

    let heightWithPadding = pageSize.height
      - ((paddingBlockStart + paddingBlockEnd) / (pageSize.landscape ? 1 : 1.681)); // if it's in landscape make the padding bigger.
    let widthWithPadding = pageSize.width
      - ((paddingInlineStart + paddingInlineEnd) / (pageSize.landscape ? 1 : 1.681));

    heightWithPadding = heightWithPadding < 0 ? 0 : Math.trunc(heightWithPadding)
    widthWithPadding = widthWithPadding < 0 ? 0 : Math.trunc(widthWithPadding)


    const pageSizeWithPadding: PageSizeAtomType = {
      onePercentHeight: heightWithPadding / 100,
      onePercentWidth: widthWithPadding / 100,
      baseFontSize: pageSize.landscape ? heightWithPadding / 100 : Math.trunc(heightWithPadding / 128),
      baseFontSizeRaw: pageSize.baseFontSizeRaw,
      containerX: pageSize.containerX,
      containerY: pageSize.containerY,
      forceSingleColumn: pageSize.forceSingleColumn,
      gridXWidth: widthWithPadding / 100,
      gridYHeight: heightWithPadding / 100,
      documentHeight: pageSize.documentHeight,
      documentWidth: pageSize.documentWidth,
      height: heightWithPadding,
      landscape: pageSize.landscape,
      width: widthWithPadding,
      x0: pageSize.x0 + paddingInlineStart,
      y0: pageSize.y0 + paddingBlockStart,
    };

    // padding only for desktop and scroll cards. On mobile take up as much space as possible.
    if (pageSize.landscape) return pageSizeWithPadding;
    if (!pageSize.landscape && navigationType === "scrollCards") return pageSizeWithPadding
    return pageSize

  },
});



export const PreviousPageIndex = atom<number>({
  key: "VS_PreviousPageIndex",
  default: -1
});

//keep track of the previously rendered pages
//prerendering is only possible in the stand alone viewer not when in the author
export const PreviouslyRenderedPages = atomFamily<boolean, number>({
  key: "VS_PreviouslyRenderedPages",
  default: false
})


// store all previously rendered pages. This should make things faster but more memory intensive
// key id pageIdx
export const RenderedPageElementsToDisplay = atomFamily<ReactElement<ElementProps>[], number>({
  key: "VS_RenderedPageElementsToDisplay ",
  default: []
});

export const StickyHeaderElementsFamily = atomFamily<PageElementData, number>({
  key: "VS_StickyHeaderElements",
  default: {
    background: "unset",
    content: "",
    id: -1,
    tapped: false,
    type: "blank:",
    firstElement: false,
    lastElement: false
  },
});

export const StickyHeaderElementsToDisplay = atom<ReactElement<ElementProps>[]>({
  key: "VS_StickyHeaderElementsToDisplay",
  default: [],
});

export const StickyFooterElementsToDisplay = atom<ReactElement<ElementProps>[]>({
  key: "VS_StickyFooterElementsToDisplay",
  default: [],
});


// place the ids of the elements into containers of tapiness
// this means the TapIndex will map to each container
export const TapIdContainers = atomFamily<TapIdContainer[], number>({
  key: "VS_TapIdContainers",
  default: [{ containerTapped: true, ids: [] }],
});